/**
 * 案件の画面のタブ「工程」（#100、docs/39 §3.1）
 *   工程表が無い：「工程表を作る」（主役）→ 作って工程表の画面へ
 *   ある：小さな数字4つ（工期・進捗・次の節目・終わり）＋ 出来高・遅れ（#101。金額のある工程・遅れがあるときだけ）＋「工程表を開く」
 *   オプションが使えない会社（受付開始のあと）：「工程表はオプション（月額 2,980円・税抜）です」＋料金プランへ
 */
import { lastMilestoneOf, mdwOf, nextMilestoneOf, type GroupDetail, type ScheduleView } from "@genbaryoku/contract";
import { SCHEDULE_MSG } from "./msg.js";
import { yen } from "../../lib/num.js";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, messageOf } from "../../api.js";
import { ErrorText } from "../../components/ui.jsx";
import { todayLocal } from "./timeline.js";
import { PcLink } from "../../components/PcLink.jsx";
import { useLabels } from "../../labels.js";

export function ScheduleTab({ g, available }: { g: GroupDetail; available: boolean }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const L = useLabels();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const b = g.schedule ?? null;
  const open = `/groups/${g.id}/schedule`;

  if (!available) {
    return (
      <div role="tabpanel" id="gpanel-schedule" aria-labelledby="gtab-schedule" className="gpanel">
        <div className="gempty" data-testid="sch-tab-option">
          <p style={{ margin: "0 0 10px" }}>{SCHEDULE_MSG.optionOff}</p>
          <PcLink className="btn" to="/account/plan">料金プランへ</PcLink>
        </div>
      </div>
    );
  }
  if (!b) {
    return (
      <div role="tabpanel" id="gpanel-schedule" aria-labelledby="gtab-schedule" className="gpanel">
        <div className="gempty">
          <button
            type="button"
            className="btn primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setErr(undefined);
              try {
                await api<ScheduleView>("POST", `/groups/${g.id}/schedule`);
                // 工程表の画面は開いたときに読み直す（作った直後の空の工程表を控えに残さない）
                qc.removeQueries({ queryKey: ["schedule", g.id] });
                void qc.invalidateQueries({ queryKey: ["group", g.id] });
                nav(open);
              } catch (e) {
                setErr(messageOf(e));
                setBusy(false);
              }
            }}
            data-testid="sch-tab-create"
          >
            {SCHEDULE_MSG.createFromEstimate}
          </button>
          <ErrorText>{err}</ErrorText>
        </div>
      </div>
    );
  }
  const today = todayLocal();
  const next = nextMilestoneOf(b, today);
  const last = lastMilestoneOf(b);
  const end = last?.date ?? b.end;
  return (
    <div role="tabpanel" id="gpanel-schedule" aria-labelledby="gtab-schedule" className="gpanel">
      <div className="sch-brief" data-testid="sch-tab-brief">
        <div>
          <span className="t">{L.period}</span>
          <b>{b.start && b.end ? `${mdwOf(b.start)}〜${mdwOf(b.end)}` : "—"}</b>
        </div>
        <div>
          <span className="t">進捗</span>
          {/* #292：％ OFF の会社（既定）は「完了 n／m{task}」 */}
          <b className="num" data-testid="sch-tab-progress">{b.pct === false ? (b.taskCount ? SCHEDULE_MSG.doneOf(b.doneCount ?? 0, b.taskCount) : "—") : b.progress == null ? "—" : `${b.progress}%`}</b>
        </div>
        <div>
          <span className="t">次の節目</span>
          <b>{next ? `${next.name || "（名前なし）"} ${mdwOf(next.date)}` : "—"}</b>
        </div>
        <div>
          <span className="t">{last ? last.name || "最後の節目" : "終わり"}</span>
          <b>{end ? mdwOf(end) : "—"}</b>
        </div>
        {b.earned ? (
          <div className="sch-internal" data-testid="sch-tab-earned">
            <span className="t">出来高</span>
            <b className="num">
              ¥{yen(b.earned.earned)}（{b.earned.total ? Math.floor((b.earned.earned * 100) / b.earned.total) : 0}%）
            </b>
          </div>
        ) : null}
        {b.maxDelay > 0 ? (
          <div data-testid="sch-tab-delay">
            <span className="t">遅れ</span>
            <b className="shu">⚠ {SCHEDULE_MSG.delayBadge(b.maxDelay)}</b>
          </div>
        ) : null}
        <span className="sp" />
        <Link className="btn primary" to={open} data-testid="sch-tab-open">
          {SCHEDULE_MSG.openSchedule}
        </Link>
      </div>
    </div>
  );
}
